import { Box, Typography } from "@mui/material";
import { CommonStyle, ControlledTable, Loader } from "Components";
import CstmBtn from "Components/CstmBtn";
import { RouteUrls } from "Constant/RouteUrls";
import { FC, Fragment, useEffect, useState } from "react";
import { CheckNumber, CheckStringEmpty } from "Utils/common";
import CommonFormatter from "Utils/Common.Formatter";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  PreApprovedOptionType,
  PreApprovedPlanType,
} from "DataTypes/PatientManagement.type";
import PreApprovedPlanApi from "Service/PreApprovedPlan.api";
import PreApprovedPlanFormatter from "Utils/PreApprovedPlan.Formatter";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "Redux/Screen/Action";
import { ModalLayout, TableLayout } from "Layouts";
import { IcdCodeType } from "DataTypes/Services/Problem.type";
import ProblemApi from "Service/Problem.api";
import ProbFormatter from "Utils/Prob.Formatter";
import { ICDTableCol } from "./Data";
import ReusablePAPUI from "Components/ReusablePAPUI";
import NoData from "Components/NoData";

const MyPlanDetails: FC = () => {
  const dispatch = useDispatch();
  const { planId, versionId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [planData, setPlanData] = useState<PreApprovedPlanType | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [codeList, setCodeList] = useState<IcdCodeType[]>([]);
  const [planLoading, setPlanLoading] = useState(true);
  const [icdLoading, setICDLoading] = useState(true);
  const isPlanEditable = planData?.approvalStatus == "DRAFT";
  const selectedPlanVersionId = planData?.planVersionId ||
    CheckNumber(versionId || state?.planVersionId);

  useEffect(() => {
    CommonFormatter.HandleNavArr({
      id: "upload patient",
      label: "Plan Details",
      path: `/${RouteUrls.hs}/${RouteUrls.organization}` +
        `/${RouteUrls.preApprovedPlan}`,
      level: 2,
      link: RouteUrls.preApprovedPlan,
      navType: "my-items",
    });
    PlanSearch();
  }, []);

  useEffect(() => {
    if (planData?.problemId) getCodeList();
  }, [planData?.problemId])

  const PlanSearch: () => void = async () => {
    const planVersionId = CheckNumber(versionId || state?.planVersionId);
    const res = await PreApprovedPlanApi.PlanSearch({
      id1: planId,
      data: planVersionId ? { planVersionId } : undefined
    });
    if (res?.success) {
      const formattedData = PreApprovedPlanFormatter.PlanSearch(res?.data);
      setPlanData(formattedData);
    }
    setPlanLoading(false);
  };

  const handleApprovePlan: () => void = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
    const res = await PreApprovedPlanApi.ApprovePlan({
      id1: planData?.planId,
      data: {
        planVersionId: planData?.planVersionId || state?.planVersionId
      }
    });
    if (res?.success) {
      planData && setPlanData({
        ...planData,
        approvalStatus: "APPROVED",
        status: "APPROVED"
      });
      dispatch(
        setAlertData(
          res?.alert || {
            alertMessage: "Preapproved plan version approved successfully.",
            openAlert: true,
            alertVariant: "success",
          }
        )
      );
    }
    setOpenModal(false);
    dispatch(stopLoading());
  };

  const handleAddUpdateOption: (
    data: PreApprovedOptionType,
    catType: string
  ) => Promise<boolean> = async (editData, catType) => {
    const payload = {
      ...editData,
      planId: parseInt(CheckStringEmpty(planId)),
      categoryId: planData?.category.find(
        catEl => catEl.categoryType == catType
      )?.categoryId,
      categoryType: catType,
      planVersionId: selectedPlanVersionId
    }
    const res = await PreApprovedPlanApi.AddUpdateOption({ data: payload });
    if (res?.success) {
      const newOptId = CheckNumber(res?.data?.id);
      planData && setPlanData({
        ...planData,
        category: planData.category.map(catEl => {
          if (catEl.categoryType == catType && editData) {
            if (editData?.id) {
              catEl.options = catEl.options.map(optEl => {
                if (optEl.id == editData.id) optEl = editData
                return optEl;
              })
            } else catEl.options.push({ ...editData, id: newOptId })
          }
          return catEl;
        })
      })
      return true;
    } else return false
  };

  const handleDeleteOption: (
    deleteOptId: number | null,
    catType: string
  ) => void = async (deleteOptId, catType) => {
    const res = await PreApprovedPlanApi.DeleteOption({
      id1: deleteOptId,
      data: {
        careplanId: parseInt(CheckStringEmpty(planId)),
        planVersionId: selectedPlanVersionId
      }
    });
    if (res?.success) {
      planData && setPlanData({
        ...planData,
        category: planData.category.map(catEl => {
          if (catEl.categoryType == catType && deleteOptId) {
            catEl.options = catEl.options.filter(
              optEl => optEl.id != deleteOptId
            );
          }
          return catEl;
        })
      });
    }
  };

  const getCodeList: () => void = async () => {
    const res = await ProblemApi.icdCodeList({
      id1: planData?.problemId,
    });
    if (res?.success) setCodeList(ProbFormatter.IcdCodes(res?.data));
    setICDLoading(false);
  };

  const TblBodyJSX = (
    <ControlledTable.TblBody
      tableColumn={() => ICDTableCol}
      tableRow={codeList}
      cellType="border"
    />
  );

  return (
    <Box height="91%" sx={{ ...CommonStyle.sxWhiteBox, overflow: "auto" }}>
      <ModalLayout
        modwidth="33%"
        heading={"Set as Preapproved Plan?"}
        content={
          <Fragment>
            <Typography>
              {"You are about to set this plan as" +
                " the Preapproved Plan. Once confirmed:"}
              <ul>
                <li>
                  {"The In Use version will be disabled" +
                    " and this version will become active."}
                </li>
                <li>
                  This version will be used for all
                  <b> new patient enrollments </b>
                  for this problem from now onwards.
                </li>
              </ul>
            </Typography>
            <Typography>
              <b>Do you wish to proceed?</b>
            </Typography>
          </Fragment>
        }
        open={openModal}
        onClose={() => setOpenModal(false)}
        buttonArr={[
          {
            id: "approve-btn",
            label: "Approve This Version",
            clickHandler: handleApprovePlan
          },
          {
            id: "cancel",
            label: "Cancel",
            clickHandler: () => setOpenModal(false),
            type: "border"
          }
        ]}
      />
      {planLoading ? <Loader /> : planData ?
        <Box sx={{ p: 2 }}>
          <Typography mb={1} variant="body2">
            <b>Plan For :</b> {planData?.problemName}
          </Typography>
          <Typography mb={2} variant="body2">
            <b>Plan Status :</b> {planData?.approvalStatus}
          </Typography>
          <Box display="flex">
            <Box width={"50%"}>
              <ReusablePAPUI
                handleAddUpdateOption={handleAddUpdateOption}
                handleDeleteOption={handleDeleteOption}
                isPlanEditable={isPlanEditable}
                planData={planData}
              />
              <Box
                display="flex"
                justifyContent="flex-end"
                sx={{ ">button": { mr: 1 } }}
              >
                <CstmBtn
                  label="Back"
                  btnType="border"
                  onClick={() => navigate(-1)}
                  sxProps={{ mt: 1, p: 1 }}
                />
                {isPlanEditable &&
                  <CstmBtn
                    label="Approve Plan"
                    onClick={() => setOpenModal(true)}
                    sxProps={{ mt: 1, p: 1 }}
                  />
                }
              </Box>
            </Box>
            <Box width="50%" ml={5}>
              <TableLayout
                tblBody={TblBodyJSX}
                tblHead={ControlledTable.TblHead(ICDTableCol)}
                height={"93%"}
                loading={icdLoading}
                pageRecordCount={codeList.length}
              />
            </Box>
          </Box>
        </Box>
        :
        <NoData />}
    </Box>
  )
};

export default MyPlanDetails;
